import {
  forwardRef,
  useId,
  type InputHTMLAttributes,
  type ReactNode,
} from 'react';
import { cn } from '../../utils/cn';
import styles from './Field.module.css';
import type { FieldErrorLike } from './fieldUtils';

export interface DatePickerProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label?: ReactNode;
  error?: FieldErrorLike | undefined;
  helper?: ReactNode;
  required?: boolean;
  /** YYYY-MM-DD inclusive lower bound. */
  minDate?: string;
  /** YYYY-MM-DD inclusive upper bound. */
  maxDate?: string;
}

export const DatePicker = forwardRef<HTMLInputElement, DatePickerProps>(function DatePicker(
  { label, error, helper, required, id: idProp, className, minDate, maxDate, ...rest },
  ref,
) {
  const reactId = useId();
  const id = idProp ?? `date-${reactId}`;
  const errorMsg = error?.message;
  const errorId = errorMsg ? `${id}-error` : undefined;
  const helperId = helper ? `${id}-helper` : undefined;
  const describedBy = [errorId, helperId].filter(Boolean).join(' ') || undefined;

  return (
    <div className={cn(styles.wrapper, className)}>
      {label !== undefined ? (
        <label htmlFor={id} className={styles.label}>
          {label}
          {required ? (
            <span className={styles.required} aria-hidden>
              *
            </span>
          ) : null}
        </label>
      ) : null}
      <input
        ref={ref}
        id={id}
        type="date"
        min={minDate}
        max={maxDate}
        aria-invalid={errorMsg !== undefined || undefined}
        aria-describedby={describedBy}
        aria-required={required || undefined}
        className={cn(styles.control, errorMsg !== undefined && styles.invalid)}
        {...rest}
      />
      {errorMsg ? (
        <p id={errorId} className={styles.error} role="alert">
          {errorMsg}
        </p>
      ) : helper ? (
        <p id={helperId} className={styles.helper}>
          {helper}
        </p>
      ) : null}
    </div>
  );
});
